// The admin "Xero contact" naming check + correction (Session ADV).
//
// A customer's Xero contact is named for the COMPANY, with the billing contact as the primary
// person. The invoice paths do that when they CREATE a contact, but a customer already synced to
// Xero keeps its old, wrongly-named contact (its ContactID is cached on the customer row and
// short-circuits the lookup). This modal is how the office fixes one.
//
// It NEVER writes on open: it reads what Xero calls the contact today, shows that against what it
// would become, and only then offers the button. An admin presses it per customer; nothing here
// runs on a schedule and nothing is corrected in bulk, because it changes live accounting data.
import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "../../lib/api";
import type { XeroContactPreview } from "../../lib/api";
import { ModalShell, MODAL_ACTIONS, MODAL_ACTION_BTN } from "../../components/ModalShell";
import { Button } from "../../components/Button";

function value(v: string | null | undefined): string {
  return v && v.trim() ? v : "Not set";
}

// One "Field | before | after" row. The after value is highlighted green only when it actually
// differs, so the office's eye lands on the real change rather than on every row.
function DiffRow({ label, before, after }: { label: string; before: string | null; after: string | null }) {
  const changed = (before ?? "") !== (after ?? "");
  return (
    <div className="grid grid-cols-[6rem_1fr] gap-x-3 gap-y-1 py-2 sm:grid-cols-[7rem_1fr_1fr]">
      <div className="font-menu text-[10px] uppercase tracking-wide text-ele-body">{label}</div>
      <div className="text-sm text-ele-body line-through decoration-ele-body/50 sm:no-underline">{value(before)}</div>
      <div className={`text-sm ${changed ? "text-ele-green" : "text-ele-body"}`}>{value(after)}</div>
    </div>
  );
}

export function XeroContactModal({ customerId, onClose }: { customerId: string; onClose: () => void }) {
  const [preview, setPreview] = useState<XeroContactPreview | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState("loading");
    setError(null);
    try {
      setPreview(await api.customers.xeroContactPreview(customerId));
      setState("ready");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not check the Xero contact.");
      setState("error");
    }
  }, [customerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const apply = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await api.customers.xeroContactResync(customerId);
      setDone(res.changed ? "The Xero contact has been renamed." : "The Xero contact was already correct, so nothing changed.");
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not update the Xero contact.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell ariaLabel="Xero contact naming" onClose={() => !saving && onClose()} width="max-w-[560px]">
      <h2 className="font-heading text-xl tracking-wide text-ele-strong">Xero contact</h2>
      <p className="mt-1 text-sm text-ele-body">
        The contact in Xero is named for the company, with the billing contact as the named person.
      </p>

      {state === "loading" && <p className="mt-5 text-sm text-ele-body">Checking Xero...</p>}

      {state === "ready" && preview && !preview.linked && (
        <p className="mt-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-4 text-sm text-ele-body">
          This customer has not been sent to Xero yet, so there is no contact to correct. The next invoice creates one, named correctly.
        </p>
      )}

      {state === "ready" && preview && preview.linked && preview.found === false && (
        <p role="alert" className="mt-5 rounded-ele border border-amber-500 bg-amber-500/10 p-4 text-sm text-amber-300">
          Xero has no contact with this customer's stored id. It may have been deleted in Xero, so there is nothing to rename.
        </p>
      )}

      {state === "ready" && preview && preview.linked && preview.found && preview.current && preview.proposed && (
        <>
          <div className="mt-5 rounded-ele border border-ele-purple-accent bg-ele-surface px-4 py-2">
            <div className="hidden grid-cols-[7rem_1fr_1fr] gap-x-3 border-b border-ele-purple-accent pb-2 sm:grid">
              <div />
              <div className="font-menu text-[10px] uppercase tracking-wide text-ele-body">In Xero now</div>
              <div className="font-menu text-[10px] uppercase tracking-wide text-ele-body">After the update</div>
            </div>
            <DiffRow label="Name" before={preview.current.name} after={preview.proposed.name} />
            <DiffRow label="First name" before={preview.current.firstName} after={preview.proposed.firstName} />
            <DiffRow label="Last name" before={preview.current.lastName} after={preview.proposed.lastName} />
          </div>

          {!preview.wouldChange && !done && (
            <p className="mt-4 text-sm text-ele-body">This contact is already named correctly. There is nothing to change.</p>
          )}
          {preview.wouldChange && (
            <p className="mt-4 text-sm text-ele-body">
              This renames the existing contact in Xero. It keeps its invoices and history: nothing is created and nothing is deleted.
            </p>
          )}
        </>
      )}

      {done && (
        <p role="status" className="mt-4 rounded-ele border border-ele-green bg-ele-green/10 px-4 py-3 text-sm text-ele-green">
          {done}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-4 text-sm text-ele-danger">
          {error}
        </p>
      )}

      <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
        <button
          onClick={onClose}
          disabled={saving}
          className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong disabled:opacity-50 ${MODAL_ACTION_BTN}`}
        >
          Close
        </button>
        {state === "error" && (
          <button
            onClick={() => void load()}
            className={`rounded-ele border border-ele-blue px-4 py-2 font-menu text-xs tracking-wide text-ele-blue transition hover:bg-ele-inset ${MODAL_ACTION_BTN}`}
          >
            Try again
          </button>
        )}
        {state === "ready" && preview?.wouldChange && !done && (
          <Button size="sm" className={MODAL_ACTION_BTN} onClick={() => void apply()} disabled={saving}>
            {saving ? "Updating..." : "Update Xero contact"}
          </Button>
        )}
      </div>
    </ModalShell>
  );
}
